import React, { useEffect, useMemo, useState } from "react";

export default function ImageCarousel({
  images = [],
  alt = "Product image",
  aspect = "4/3", // or "1/1"
  className = "",
  showDots = true,
  showCounter = true,
  imgClassName = "",
  loading = "lazy",
  decoding = "async",
}) {
  const safeImages = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []),
    [images]
  );

  const [index, setIndex] = useState(0);

  // If the images array changes (new upload / fetch), clamp index.
  useEffect(() => {
    if (index > safeImages.length - 1) setIndex(0);
  }, [safeImages.length, index]);

  const hasMany = safeImages.length > 1;

  const goPrev = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!hasMany) return;
    setIndex((i) => (i - 1 + safeImages.length) % safeImages.length);
  };

  const goNext = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!hasMany) return;
    setIndex((i) => (i + 1) % safeImages.length);
  };

  const onKeyDown = (e) => {
    if (!hasMany) return;
    if (e.key === "ArrowLeft") goPrev(e);
    if (e.key === "ArrowRight") goNext(e);
  };

  // If your card is clickable (opens product), stopping propagation above prevents arrows from triggering the card click.
  const src = safeImages[index];

  return (
    <div
      className={`relative overflow-hidden rounded-xl ${className}`}
      tabIndex={hasMany ? 0 : -1}
      onKeyDown={onKeyDown}
      aria-label={hasMany ? "Image carousel" : "Image"}
      style={{
        // quick aspect-ratio support without relying on Tailwind plugin
        aspectRatio: aspect.replace("/", " / "),
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className={`h-full w-full object-cover select-none ${imgClassName}`}
          draggable={false}
          loading={loading}
          decoding={decoding}
        />
      ) : (
        <div className="h-full w-full bg-white/5 flex items-center justify-center text-white/50 text-sm">
          No image
        </div>
      )}

      {hasMany && (
        <>
          {/* Prev */}
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/50 hover:bg-black/65 text-white flex items-center justify-center backdrop-blur"
            aria-label="Previous image"
          >
            ‹
          </button>

          {/* Next */}
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/50 hover:bg-black/65 text-white flex items-center justify-center backdrop-blur"
            aria-label="Next image"
          >
            ›
          </button>

          {/* Counter (optional but super useful) */}
          {showCounter && (
            <div className="absolute bottom-2 right-2 text-xs text-white bg-black/45 px-2 py-1 rounded-full backdrop-blur">
              {index + 1}/{safeImages.length}
            </div>
          )}

          {/* Dots */}
          {showDots && safeImages.length <= 8 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {safeImages.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIndex(i);
                  }}
                  className={`h-1.5 w-1.5 rounded-full ${
                    i === index ? "bg-white" : "bg-white/40"
                  }`}
                  aria-label={`Go to image ${i + 1}`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
